import _extends from "@babel/runtime/helpers/extends";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _class2, _descriptor, _descriptor2, _AbYearInput;
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbList, incrementSelection } from "abreact/input/AbList";
import { AbPlaceholder } from "abreact/input/AbPlaceholder";
import { AbTextInput } from "abreact/input/AbTextInput";
import { AbButton } from "abreact/misc/AbButton";
import { AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { KeyboardUtils, KeyValue } from "abweb/utils/KeyboardUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { action, computed, observable, runInAction, makeObservable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
/**
 * A React-based implementation of a year input (with filtering) dropdown component.
 *
 * Uses a virtualized list to display years
 */
export let AbYearInput = observer(_class = (_class2 = (_AbYearInput = class AbYearInput extends React.Component {
  constructor(abYearInputProps) {
    super(abYearInputProps);
    _defineProperty(this, "DEFAULT_MAX_YEAR", 9999);
    _defineProperty(this, "DEFAULT_MIN_YEAR", 100);
    _defineProperty(this, "defaultInputID", TypeUtils.generateUniqueId("ab-year-input"));
    _defineProperty(this, "listId", TypeUtils.generateUniqueId("year-list"));
    _defineProperty(this, "dropdownElement", null);
    _initializerDefineProperty(this, "popupShown", _descriptor, this);
    _initializerDefineProperty(this, "filterValue", _descriptor2, this);
    _defineProperty(this, "items", AbListHelper.range(this.maxYear, this.minYear - 1, -1));
    _defineProperty(this, "filterFunction", (item, filterValue) => {
      return item.toString().indexOf(filterValue.toLowerCase()) === 0;
    });
    _defineProperty(this, "showPopup", action(() => {
      this.popupShown = true;
      this.filterValue = "";
    }));
    _defineProperty(this, "hidePopup", () => {
      if (this.popupShown) {
        runInAction(() => {
          this.popupShown = false;
          this.filterValue = "";
        });
        this.dropdownElement && FocusUtils.setFocus(this.dropdownElement);
      }
    });
    // finds nearest year to current year
    // returns -1 if there is no possible nearest year
    _defineProperty(this, "calculateNearestYear", () => {
      const max_year_length = this.maxYear.toString().length;
      const currentYear = new Date().getFullYear();
      if (this.filterValue === "") {
        // the nearest year is just the current year if no filter
        return currentYear;
      } else if (!NumberUtils.isNumber(this.filterValue) && this.filterValue !== "" || this.filterValue.length > max_year_length || parseInt(this.filterValue, 10) > this.maxYear) {
        // no items if filterValue is greater than max year or not a number
        return -1;
      } else if (this.filterValue.length === max_year_length) {
        // only one possible item if filterValue is same length as max year
        return parseInt(this.filterValue, 10);
      }
      // binary search on filteredItems (which is a sorted in reverse chronological order) to find the nearest year
      return AbListHelper.findClosestValue(this.filteredItems.reverse(), currentYear) ?? -1;
    });
    _defineProperty(this, "itemRenderer", item => {
      if (item === undefined) {
        return /*#__PURE__*/React.createElement(AbPlaceholder, {
          placeholder: this.props.placeholder
        });
      }
      return item.toString();
    });
    _defineProperty(this, "onKeyDown", action(event => {
      // close popup and discard selection on escape
      if (this.props.disabled !== true) {
        if (event.key === KeyValue.TAB && this.popupShown) {
          this.hidePopup();
        } else if (event.key === KeyValue.ESCAPE) {
          this.hidePopup();
          event.stopPropagation();
          event.preventDefault();
        } else if (event.key === KeyValue.ENTER || event.key === KeyValue.SPACE) {
          if (this.popupShown) {
            if (this.filteredItems.size === 1) {
              this.props.onChange(this.filteredItems.itemAt(0));
            }
            this.hidePopup();
          } else {
            this.showPopup();
          }
          event.stopPropagation();
          event.preventDefault();
        } else if (event.key === KeyValue.ARROW_DOWN || event.key === KeyValue.ARROW_UP) {
          if (!this.popupShown) {
            const valueIndex = this.props.value ? this.filteredItems.findIndexOf(this.props.value) : -1;
            const newSelection = incrementSelection(AbListHelper.from(this.filteredItems), valueIndex === -1 ? undefined : valueIndex, event.key === KeyValue.ARROW_DOWN ? 1 : -1);
            if (newSelection) {
              this.props.onChange(AbEnriched.getItem(newSelection[1]));
            }
          } else {
            KeyboardUtils.simulateKeyDown(event.key, event.currentTarget.ownerDocument.getElementById(this.listId));
          }
        }
      }
      return false;
    }));
    _defineProperty(this, "onButtonClick", action(e => {
      e.stopPropagation();
      (this.popupShown ? this.hidePopup : this.showPopup)();
    }));
    _defineProperty(this, "onBackgroundCaptureMouseDown", event => {
      // Only close the popup if the click is in the background, not on the dropdown itself
      if (!this.dropdownElement?.contains(event.target)) {
        this.hidePopup();
      }
    });
    _defineProperty(this, "onInputChange", action(value => {
      this.filterValue = value;
    }));
    _defineProperty(this, "renderList", () => {
      if (this.filteredItems.size > 0) {
        // if there are matching items in the filter list, render list as normal
        return /*#__PURE__*/React.createElement(AbList, {
          className: "ab-year-input-list",
          virtualized: true,
          selectionMode: SelectionMode.SINGLE,
          scrollToIndex: this.scrollToIndex,
          items: this.filteredItems,
          itemRenderer: this.itemRenderer,
          value: this.props.value,
          onChange: i => {
            this.props.onChange(i);
            this.hidePopup();
          },
          autoFocus: true,
          id: this.listId
        });
      } else {
        // display message if no items match the filter
        return /*#__PURE__*/React.createElement("div", {
          className: "ab-year-input-no-items-message"
        }, AbReactResources.datePicker_yearInput_noItems());
      }
    });
    makeObservable(this);
    this.popupShown = false;
    this.filterValue = "";
  }
  get maxYear() {
    return this.props.maxYear ? this.props.maxYear : this.DEFAULT_MAX_YEAR;
  }
  get minYear() {
    return this.props.minYear ? this.props.minYear : this.DEFAULT_MIN_YEAR;
  }
  get filteredItems() {
    if (this.filterValue === "") {
      return AbListHelper.from(this.items);
    }
    return AbListHelper.from(AbListHelper.filter(this.items, v => this.filterFunction(v, this.filterValue)).toArray());
  }
  get scrollToIndex() {
    if (this.filterValue === "" && this.props.value) {
      return this.filteredItems.findIndexOf(this.props.value);
    } else {
      return this.filteredItems.findIndexOf(this.calculateNearestYear());
    }
  }
  render() {
    const contextSize = this.context;
    const {
      disabled,
      autoFocus,
      ...inputProps
    } = ReactUtils.inputProps("ab-year-input", this.props, [this.props.size ?? contextSize]);
    const inner = /*#__PURE__*/React.createElement(AbWithTooltip, {
      tooltip: this.props.tooltip
    }, /*#__PURE__*/React.createElement("div", _extends({}, inputProps, {
      id: this.props.id ?? this.defaultInputID,
      ref: element => {
        this.dropdownElement = element;
        if (element && autoFocus) {
          FocusUtils.setFocus(element);
        }
      },
      onKeyDown: this.onKeyDown,
      onClick: action(() => !disabled && !this.popupShown && this.showPopup())
    }), !this.popupShown && /*#__PURE__*/React.createElement("div", {
      className: "ab-year-input-value"
    }, this.itemRenderer(this.props.value)), this.popupShown && /*#__PURE__*/React.createElement(AbTextInput, {
      className: "ab-year-input-text-input",
      autoFocus: true,
      value: this.filterValue,
      onChange: this.onInputChange,
      disabled: disabled
    }), /*#__PURE__*/React.createElement(AbButton, {
      icon: "absdk:chevron-down",
      className: "ab-no-border-button ab-year-input-chevron-button",
      disabled: disabled,
      onClick: this.onButtonClick
    }), this.popupShown && /*#__PURE__*/React.createElement(AbPopup, {
      flushContent: true,
      onBackgroundCaptureMouseDown: this.onBackgroundCaptureMouseDown,
      className: "ab-year-input-popup",
      overlay: false,
      onClose: action(() => {
        this.popupShown = false;
      }),
      closeOnTabOut: true,
      positioner: PopupUtils.VERTICAL_POSITIONER,
      origin: this.dropdownElement ?? undefined
    }, this.renderList())));
    return sizeProviderWrapper(inner, this.props, contextSize);
  }
}, _defineProperty(_AbYearInput, "contextType", AbSizeContext), _AbYearInput), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "popupShown", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "filterValue", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "filteredItems", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "filteredItems"), _class2.prototype), _class2)) || _class;
//# sourceMappingURL=AbYearInput.js.map
